import React from 'react';
import { Form, Input, Select, Row, Col, DatePicker, Upload, Button } from 'antd';
import { Upload as UploadIcon, ChevronDown } from 'lucide-react';
import styles from '../../pages/UpdatePersonalProfilePage.module.css';

const { Option } = Select;
const { TextArea } = Input;

interface UpdatePersonalInfoSectionProps {
  uploading: boolean;
  profileImage: string | null;
  setProfileImage: (image: string | null) => void;
  handleImageUpload: (file: File) => Promise<boolean>;
}

export const UpdatePersonalInfoSection: React.FC<UpdatePersonalInfoSectionProps> = ({
  uploading,
  profileImage,
  setProfileImage,
  handleImageUpload,
}) => {
  return (
    <>
      <Row gutter={24}>
        <Col span={6}>
          <Upload
            beforeUpload={handleImageUpload}
            showUploadList={false}
            accept="image/*"
            disabled={uploading}
          >
            <div className={styles.profileImageContainer}>
              {profileImage && profileImage.trim() !== '' ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className={styles.profileImage}
                  onError={() => {
                    setProfileImage(null);
                  }}
                />
              ) : (
                <div className={styles.profileImagePlaceholder}>
                  <UploadIcon size={32} className={styles.uploadIcon} />
                  <span className={styles.uploadText}>Cập nhật ảnh đại diện</span>
                </div>
              )}
              <Button
                type="link"
                icon={<UploadIcon size={16} />}
                className={styles.uploadButton}
                onClick={(e) => e.stopPropagation()}
                loading={uploading}
                disabled={uploading}
              >
                {uploading ? 'Đang tải...' : 'Tải ảnh lên'}
              </Button>
            </div>
          </Upload>
        </Col>

        <Col span={18}>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                name="fullName"
                label="Họ và tên"
                rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
              >
                <Input placeholder="Họ và tên" size="large" className={styles.input} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="Mô tả bản thân">
                <TextArea
                  placeholder="Mô tả bản thân"
                  rows={4}
                  size="large"
                  className={`${styles.input} ${styles.textareaNoResize}`}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={24} className={styles.formRow}>
        <Col span={8}>
          <Form.Item name="placeOfBirth" label="Nơi sinh">
            <Input placeholder="Ví dụ: Quận 7" size="large" className={styles.input} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="hometown" label="Nguyên quán">
            <Input placeholder="TP.HCM" size="large" className={styles.input} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="nationality" label="Quốc tịch">
            <Input placeholder="Việt Nam" size="large" className={styles.input} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24} className={styles.formRow}>
        <Col span={8}>
          <Form.Item name="ethnicity" label="Dân tộc">
            <Select
              placeholder="Dân tộc"
              size="large"
              className={styles.select}
              suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
              showSearch
              allowClear
              mode="tags"
              maxTagCount={1}
              filterOption={(input, option) =>
                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value="Kinh">Kinh</Option>
              <Option value="Tày">Tày</Option>
              <Option value="Thái">Thái</Option>
              <Option value="Mường">Mường</Option>
              <Option value="Khmer">Khmer</Option>
              <Option value="Hoa">Hoa</Option>
              <Option value="Nùng">Nùng</Option>
              <Option value="H'Mông">H'Mông</Option>
              <Option value="Dao">Dao</Option>
              <Option value="Gia Rai">Gia Rai</Option>
              <Option value="Ê Đê">Ê Đê</Option>
              <Option value="Ba Na">Ba Na</Option>
              <Option value="Xơ Đăng">Xơ Đăng</Option>
              <Option value="Sán Chay">Sán Chay</Option>
              <Option value="Cơ Ho">Cơ Ho</Option>
              <Option value="Chăm">Chăm</Option>
              <Option value="Sán Dìu">Sán Dìu</Option>
              <Option value="Hrê">Hrê</Option>
              <Option value="Ra Glai">Ra Glai</Option>
              <Option value="Mnông">Mnông</Option>
              <Option value="Thổ">Thổ</Option>
              <Option value="Xtiêng">Xtiêng</Option>
              <Option value="Bru - Vân Kiều">Bru - Vân Kiều</Option>
              <Option value="Giáy">Giáy</Option>
              <Option value="Cơ Tu">Cơ Tu</Option>
              <Option value="Gié - Triêng">Gié - Triêng</Option>
              <Option value="Mạ">Mạ</Option>
              <Option value="Co">Co</Option>
              <Option value="Tà Ôi">Tà Ôi</Option>
              <Option value="Chơ Ro">Chơ Ro</Option>
              <Option value="Kháng">Kháng</Option>
              <Option value="Xinh Mun">Xinh Mun</Option>
              <Option value="Hà Nhì">Hà Nhì</Option>
              <Option value="Chu Ru">Chu Ru</Option>
              <Option value="Lào">Lào</Option>
              <Option value="La Chí">La Chí</Option>
              <Option value="Phù Lá">Phù Lá</Option>
              <Option value="La Hủ">La Hủ</Option>
              <Option value="Khơ Mú">Khơ Mú</Option>
              <Option value="Lự">Lự</Option>
              <Option value="Pà Thẻn">Pà Thẻn</Option>
              <Option value="Lô Lô">Lô Lô</Option>
              <Option value="Chứt">Chứt</Option>
              <Option value="Mảng">Mảng</Option>
              <Option value="Cờ Lao">Cờ Lao</Option>
              <Option value="Bố Y">Bố Y</Option>
              <Option value="Cống">Cống</Option>
              <Option value="Ngái">Ngái</Option>
              <Option value="Si La">Si La</Option>
              <Option value="Pu Péo">Pu Péo</Option>
              <Option value="Rơ Măm">Rơ Măm</Option>
              <Option value="Brâu">Brâu</Option>
              <Option value="Ơ Đu">Ơ Đu</Option>
              <Option value="Khác">Khác</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="dateOfBirth" label="Ngày sinh">
            <DatePicker
              placeholder="Chọn ngày"
              size="large"
              className={styles.input}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="gender" label="Giới tính">
            <Select
              placeholder="Chọn giới tính"
              size="large"
              className={styles.select}
              suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
            >
              <Option value="Nam">Nam</Option>
              <Option value="Nữ">Nữ</Option>
              <Option value="Khác">Khác</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24} className={styles.formRow}>
        <Col span={8}>
          <Form.Item name="religion" label="Tôn giáo">
            <Select
              placeholder="Tôn giáo"
              size="large"
              className={styles.select}
              suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
              showSearch
              allowClear
              mode="tags"
              maxTagCount={1}
              filterOption={(input, option) =>
                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value="Không">Không</Option>
              <Option value="Phật giáo">Phật giáo</Option>
              <Option value="Công giáo">Công giáo</Option>
              <Option value="Tin lành">Tin lành</Option>
              <Option value="Cao đài">Cao đài</Option>
              <Option value="Phật giáo Hòa Hảo">Phật giáo Hòa Hảo</Option>
              <Option value="Hồi giáo">Hồi giáo</Option>
              <Option value="Baha'i">Baha'i</Option>
              <Option value="Tịnh độ cư sĩ Phật hội Việt Nam">Tịnh độ cư sĩ Phật hội Việt Nam</Option>
              <Option value="Đạo Mẫu">Đạo Mẫu</Option>
              <Option value="Đạo Tứ Ân Hiếu Nghĩa">Đạo Tứ Ân Hiếu Nghĩa</Option>
              <Option value="Minh Sư Đạo">Minh Sư Đạo</Option>
              <Option value="Bà-la-môn giáo">Bà-la-môn giáo</Option>
              <Option value="Ấn Độ giáo">Ấn Độ giáo</Option>
              <Option value="Do Thái giáo">Do Thái giáo</Option>
              <Option value="Khác">Khác</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="maritalStatus" label="Tình trạng hôn nhân">
            <Select
              placeholder="Tình trạng hôn nhân"
              size="large"
              className={styles.select}
              suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
            >
              <Option value="Độc thân">Độc thân</Option>
              <Option value="Đã kết hôn">Đã kết hôn</Option>
              <Option value="Ly hôn">Ly hôn</Option>
              <Option value="Góa">Góa</Option>
              <Option value="Khác">Khác</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="militaryService" label="Nghĩa vụ quân sự">
            <Select
              placeholder="Nghĩa vụ quân sự"
              size="large"
              className={styles.select}
              suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
            >
              <Option value="Đã hoàn thành">Đã hoàn thành</Option>
              <Option value="Hoãn nghĩa vụ">Hoãn nghĩa vụ</Option>
              <Option value="Miễn nghĩa vụ">Miễn nghĩa vụ</Option>
              <Option value="Khác">Khác</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24} className={styles.formRow}>
        <Col span={8}>
          <Form.Item name="personalTaxCode" label="Mã số thuế cá nhân">
            <Input placeholder="Mã số thuế cá nhân" size="large" className={styles.input} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="startDate" label="Ngày vào làm">
            <DatePicker
              placeholder="Chọn ngày"
              size="large"
              className={styles.input}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="contractSigningDate" label="Ngày ký HĐLĐ chính thức">
            <DatePicker
              placeholder="Chọn ngày"
              size="large"
              className={styles.input}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
      </Row>

      <div className={styles.section}>
        <Row gutter={24} className={styles.formRow}>
          <Col span={6}>
            <Form.Item name="educationLevel" label="Trình độ phổ thông">
              <Select
                placeholder="Trình độ phổ thông"
                size="large"
                className={styles.select}
                suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
              >
                <Option value="Tiểu học">Tiểu học</Option>
                <Option value="Trung học cơ sở">Trung học cơ sở</Option>
                <Option value="Trung học phổ thông">Trung học phổ thông</Option>
                <Option value="Khác">Khác</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="yearsOfExperience" label="Số năm kinh nghiệm">
              <Input placeholder="Số năm kinh nghiệm" size="large" className={styles.input} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="major" label="Chuyên ngành">
              <Input placeholder="Chuyên ngành" size="large" className={styles.input} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="highestEducationLevel" label="Trình độ học vấn cao nhất">
              <Input
                placeholder="Trình độ học vấn cao nhất"
                size="large"
                className={styles.input}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24} className={styles.formRow}>
          <Col span={12}>
            <Form.Item name="directManager" label="Người quản lý trực tiếp">
              <Input
                placeholder="Người quản lý trực tiếp"
                size="large"
                className={styles.input}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="laborType" label="Loại lao động">
              <Select
                placeholder="Loại lao động"
                size="large"
                className={styles.select}
                suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
              >
                <Option value="Toàn thời gian">Toàn thời gian</Option>
                <Option value="Bán thời gian">Bán thời gian</Option>
                <Option value="Hợp đồng">Hợp đồng</Option>
                <Option value="Thực tập">Thực tập</Option>
                <Option value="Khác">Khác</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </div>
    </>
  );
};

